/**
 * HolePicker — manual "current hole" controller for the course brief.
 * (BEACON-34 Phase A — GPS hole detection v0)
 *
 * Renders a per-course button grid (default 18; e.g., Shorty's=19, Bandon
 * Preserve=13) — or compact dropdown on small screens — in the brief toolbar.
 * Hole count is supplied by the caller (initialView.holes from location.json,
 * BEACON-53 audit fix). Tapping a hole:
 *   1. Calls briefLoader.showHole(n) → scroll + highlight
 *   2. Updates the picker's visual selected state
 *   3. Persists the choice in localStorage so a refresh remembers
 *
 * Phase A v0 ships pure-manual. Phase A v0.5 layers GPS on top: a separate
 * GeoController will fire `gps:hole-suggestion` events; this picker shows
 * a "📍 ~Hole N" hint badge above the buttons but doesn't auto-select
 * (the user always confirms).
 */

// BEACON-50 v0.7 (ER-COMP-5): scoped per-course so a stored Crandon hole-3
// doesn't pre-fill on a fresh Bandon Dunes load. Bumped to v2 so old v1
// entries are ignored and self-clean.
const STORAGE_KEY_PREFIX = "beacon-golf:current-hole-v2:";
const LEGACY_STORAGE_KEY = "beacon-golf:current-hole-v1";
// BEACON-53 (audit fix): hole count is now per-course (Shorty's=19,
// Bandon Preserve=13, regulation=18). Source: location.json `holes` field.
// Default 18 only when caller omits holeCount.
const DEFAULT_HOLE_COUNT = 18;

export class HolePicker {
  /**
   * @param {Object} opts
   * @param {HTMLElement} opts.container - element to render into (toolbar slot)
   * @param {import("./BriefLoader.js").BriefLoader} opts.briefLoader
   * @param {string} [opts.courseSlug] - course slug for storage scoping
   * @param {number} [opts.holeCount=18] - total holes for this course (per-course; 18 default)
   */
  constructor({ container, briefLoader, courseSlug = null, holeCount = DEFAULT_HOLE_COUNT }) {
    if (!container) throw new Error("HolePicker: container required");
    if (!briefLoader) throw new Error("HolePicker: briefLoader required");
    this.container = container;
    this.briefLoader = briefLoader;
    this.courseSlug = courseSlug;
    this.holeCount = Number.isFinite(holeCount) && holeCount > 0 ? holeCount : DEFAULT_HOLE_COUNT;
    this.allHoles = Array.from({ length: this.holeCount }, (_, i) => i + 1);
    this.currentHole = null;
    /** @type {Set<number>} holes that have a real section in the current brief */
    this.presentHoles = new Set();

    // Drop legacy v1 (un-scoped) cache eagerly so it can't leak between courses.
    try { localStorage.removeItem(LEGACY_STORAGE_KEY); } catch { /* non-fatal */ }

    this._render();

    // Listen for brief loads — re-derive presentHoles set + restore last hole.
    document.addEventListener("brief:loaded", (ev) => {
      this.presentHoles = new Set(ev.detail?.presentHoles || []);
      this._refreshButtonStates();
      this._restoreFromStorage();
    });
  }

  _storageKey() {
    return STORAGE_KEY_PREFIX + (this.courseSlug || "_default");
  }

  /** Initial DOM render. Idempotent. */
  _render() {
    this.container.innerHTML = `
      <div class="hole-picker" role="toolbar" aria-label="Current hole">
        <span class="hole-picker-label">Hole</span>
        <div class="hole-picker-grid"></div>
        <button type="button" class="hole-picker-clear" data-action="clear" aria-label="Clear current hole">×</button>
      </div>
    `;

    const grid = this.container.querySelector(".hole-picker-grid");
    for (const n of this.allHoles) {
      const b = document.createElement("button");
      b.type = "button";
      b.className = "hole-picker-btn";
      b.dataset.hole = String(n);
      b.textContent = String(n);
      b.setAttribute("aria-label", `Hole ${n}`);
      b.addEventListener("click", () => this.selectHole(n));
      grid.appendChild(b);
    }

    this.container
      .querySelector('[data-action="clear"]')
      .addEventListener("click", () => this.clearHole());
  }

  /**
   * Set the current hole (idempotent for same value). Triggers brief
   * scroll+highlight and persists to localStorage.
   */
  selectHole(n, { scroll = true, focus = true } = {}) {
    if (!Number.isFinite(n) || n < 1 || n > this.holeCount) return;
    this.currentHole = n;
    const wasReal = this.briefLoader.showHole(n, { scroll });
    this._refreshButtonStates();
    try {
      localStorage.setItem(
        this._storageKey(),
        JSON.stringify({ hole: n, ts: Date.now() })
      );
    } catch {
      /* storage may be unavailable in private mode; non-fatal */
    }
    // BEACON-50 v0.7 (ER-A11Y-3): after selecting a hole, move focus to the
    // brief-panel target so SR users land on the content they just navigated
    // to (instead of staying on the picker button).
    if (focus && wasReal) {
      const target = this.briefLoader.panel.querySelector(`#hole-${n}`);
      if (target) {
        if (!target.hasAttribute("tabindex")) target.setAttribute("tabindex", "-1");
        // Defer focus until scroll settles (smooth scroll + focus collide).
        setTimeout(() => target.focus({ preventScroll: true }), 250);
      }
    }
    this.container.dispatchEvent(
      new CustomEvent("hole:change", {
        bubbles: true,
        detail: { hole: n, hasAnnotations: wasReal },
      })
    );
  }

  clearHole() {
    this.currentHole = null;
    this._refreshButtonStates();
    // Remove highlight + ghost without scrolling
    this.briefLoader.panel
      .querySelectorAll(".hole.is-current-hole")
      .forEach((el) => el.classList.remove("is-current-hole"));
    const ghost = this.briefLoader.panel.querySelector("[data-hole-ghost]");
    if (ghost) ghost.remove();
    try {
      localStorage.removeItem(this._storageKey());
    } catch {
      /* non-fatal */
    }
  }

  _refreshButtonStates() {
    const buttons = this.container.querySelectorAll(".hole-picker-btn");
    for (const b of buttons) {
      const n = Number.parseInt(b.dataset.hole, 10);
      const isCurrent = n === this.currentHole;
      b.classList.toggle("is-current", isCurrent);
      b.classList.toggle("is-empty", !this.presentHoles.has(n));
      // BEACON-50 P1: non-color a11y cue (WCAG 1.4.1) — expose current
      // selection to assistive tech via aria-current, in addition to the
      // visual underline indicator on .is-current.
      if (isCurrent) {
        b.setAttribute("aria-current", "true");
      } else {
        b.removeAttribute("aria-current");
      }
      b.title = this.presentHoles.has(n)
        ? `Hole ${n} (has annotations)`
        : `Hole ${n} — no specific notes in current brief`;
    }
  }

  _restoreFromStorage() {
    try {
      const raw = localStorage.getItem(this._storageKey());
      if (!raw) return;
      const { hole, ts } = JSON.parse(raw);
      // BEACON-50 v0.7 (ER-COMP-5): tighter TTL — 4h instead of 12h.
      // First-paint of a brief should normally show NO selected hole; only
      // restore when the user genuinely just had this app open.
      const TTL = 4 * 60 * 60 * 1000;
      if (!hole || !ts || Date.now() - ts > TTL) {
        localStorage.removeItem(this._storageKey());
        return;
      }
      // DO NOT scroll on restore (user didn't ask) — just highlight.
      this.currentHole = hole;
      this._refreshButtonStates();
      const target = this.briefLoader.panel.querySelector(`#hole-${hole}`);
      if (target) target.classList.add("is-current-hole");
    } catch {
      /* non-fatal */
    }
  }
}
